import {
  customTitle,
  getTenantName,
  hasPermission,
  isAIDevEnv,
  isAdmin,
  isDemocollege,
  isDesignToolEnabled,
  isDevEnv,
  isLgs,
  isLinksEdu,
  isLyceum,
  isLyceumTest,
  isMyschool,
  isNewExamManagerEnabled,
  isParent,
  isProdEnv,
  isSelectiveAiDomains,
  isStaff,
  isTeacher,
  processGeneralSettingsFeatures,
} from 'common/HelperFunctions'
import {GENERAL_ENABLE_DISABLE, USER_TYPES} from 'common/constants'
import {getStudentFeePath} from 'common/helpers/fee'
import {MainReducer, UserReducer} from 'setup'

import {Link} from './types'

export const getSchoolLink = ({mainReducer}: {mainReducer: MainReducer}): Link[] => {
  const {generalSettings} = mainReducer
  const showStudentsInSelectiveAiDomain = generalSettings?.subject_wise_setting === 1
  const featuresString = generalSettings?.features || ''
  const featuresConfig = processGeneralSettingsFeatures(featuresString)
  const hideDepartment =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.department_module?.enabled === false
  const hideTags =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.tags_module?.enabled === false
  // Users sub-module flags
  const hideUserLabel =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.label_module?.enabled === false
  const hideInactiveUsers =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.inactive_user_module?.enabled === false
  const hideParents =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.parent_module?.enabled === false

  // Data Import sub-module flags
  const hideImportClassSubjectTeacher =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.class_subject_teacher_import_module?.enabled ===
      false
  const hideImportStudentParent =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.student_parent_import_module?.enabled === false
  const hideImportUserLabel =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.user_label_import_module?.enabled === false
  const hideImportTimetable =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.timetable_import_module?.enabled === false
  const hideImportAnnualCalendar =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.annual_calendar_import_module?.enabled === false
  const hideImportStudentPrevGrade =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.student_previous_grade_import_module?.enabled ===
      false
  const hideImportStudentPrevPercentages =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.student_previous_percentages_import_module
      ?.enabled === false
  const hideImportTags =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.tags_import_module?.enabled === false

  // Schedules sub-module flags
  const hideScheduleSetting =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.schedule_setting_module?.enabled === false
  const hideAnnualCalendar =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.annual_calendar_module?.enabled === false
  const hideTimetable =
    !!featuresString &&
    featuresConfig?.environmentMatch &&
    (featuresConfig as any)?.rawFeatures?.modules?.timetable_module?.enabled === false

  // ai4 tenant: show AI Timetable and CSV Import as main menus (replace Schedules submenu and Data Import)
  const showAi4SimplifiedMenu = isSelectiveAiDomains() && hasPermission('ai_timetable')

  if (isSelectiveAiDomains()) {
    return [
      {
        to: '/dashboard',
        title: 'MENU.DASHBOARD',
        fontIcon: 'fas fa-home',
        visible: hasPermission('dashboard'),
      },
      {
        to: '/institute/schedule/timetable',
        title: 'MENU.AI_SCHEDULE',
        fontIcon: 'far fa-calendar-alt',
        visible: hasPermission('ai_timetable'),
      },
      {
        to: '',
        title: 'MENU.DATA',
        fontIcon: 'fas fa-database',
        visible: true,
        subLinks: [
          {
            to: '',
            title: 'MENU.USERS',
            fontIcon: 'fas fa-users',
            visible:
              (hasPermission('user_manager_students') && showStudentsInSelectiveAiDomain) ||
              hasPermission('user_manager_teachers'),
            subLinks: [
              {
                to: '/institute/student',
                title: 'MENU.STUDENTS',
                fontIcon: 'fas fa-user',
                visible: hasPermission('user_manager_students') && showStudentsInSelectiveAiDomain,
              },
              {
                to: '/institute/parent',
                title: 'MENU.PARENTS',
                fontIcon: 'fas fa-user',
                visible: false,
              },
              {
                to: '/institute/teacher',
                title: 'MENU.TEACHERS',
                fontIcon: 'fas fa-user',
                visible: hasPermission('user_manager_teachers'),
              },
              {
                to: '/institute/staff',
                title: 'MENU.STAFF',
                fontIcon: 'fas fa-user',
                visible: false,
              },
              {
                to: '/institute/staff-role',
                title: 'MENU.STAFF_ROLES',
                fontIcon: 'fas fa-user',
                visible: false,
              },
              {
                to: '/institute/label',
                title: 'MENU.LABEL',
                fontIcon: 'fas fa-user',
                visible: false,
              },
              {
                to:
                  isProdEnv() && isLinksEdu()
                    ? '/institute/user/unadmin?tabType=student'
                    : '/institute/inactive-users/students',
                title: 'MENU.IN-ACTIVE_USERS',
                fontIcon: 'fas fa-user',
                visible: false,
              },
            ],
          },
          {
            to: '',
            title: 'MENU.CLASSES_&_SUBJECTS',
            fontIcon: 'fas fa-school',
            visible: hasPermission('class_subject_manager'),
            subLinks: [
              {
                to: '/institute/grades',
                title: 'MENU.GRADES',
                fontIcon: 'fas fa-layer-group',
                visible: hasPermission('class_subject_manager_grades'),
              },
              {
                to: '/institute/sections',
                title: 'MENU.SECTIONS',
                fontIcon: 'fas fa-th-large',
                visible: hasPermission('class_subject_manager_sections'),
              },
              {
                to: '/institute/classes',
                title: 'MENU.CLASS',
                fontIcon: 'fas fa-chalkboard',
                visible: hasPermission('class_subject_manager_classes'),
              },
              {
                to: '/institute/subjects',
                title: 'MENU.SUBJECT',
                fontIcon: 'fas fa-book',
                visible: hasPermission('class_subject_manager_subjects'),
              },
            ],
          },
          {
            to: '/institute/data-import/csv-import/upload',
            title: 'MENU.CSV_IMPORT',
            fontIcon: 'fas fa-file-excel',
            visible: hasPermission('ai_timetable'),
          },
        ],
      },
    ]
  }

  return [
    {
      to: '/dashboard',
      title: 'MENU.DASHBOARD',
      fontIcon: 'fas fa-home',
      visible: hasPermission('dashboard'),
    },

    /**
     * Administration / Data (ai4: Data menu with subLinks when isSelectiveAiDomains)
     */

    {
      type: 'label' as const,
      title: 'MENU.ADMINISTRATION',
      to: '',
      visible:
        hasPermission('admission_manager') ||
        hasPermission('attendance_manager') ||
        hasPermission('branch_level_manager') ||
        hasPermission('class_subject_manager') ||
        hasPermission('department_manager') ||
        hasPermission('digital_request_manager') ||
        hasPermission('news_manager') ||
        hasPermission('resource_manager') ||
        hasPermission('schedule_manager') ||
        hasPermission('design_tool') ||
        hasPermission('tags_manager') ||
        hasPermission('user_manager'),
    },
    {
      to: '',
      title: 'MENU.ADMISSIONS',
      fontIcon: 'fas fa-user-plus',
      visible:
        hasPermission('admission_manager') || generalSettings?.admission_form_permission === 1,
      subLinks: [
        {
          // The admissions overview (documents/12 FRD) leads the section: it is
          // the at-a-glance view the other entries drill down from. Shown to
          // whoever can edit the admission form pages - the key every Admission
          // Settings API enforces.
          to: '/institute/admission/report',
          title: 'MENU.DASHBOARD',
          fontIcon: 'fas fa-chart-pie',
          visible: hasPermission('user_manager_settings'),
        },
        {
          to: '/institute/admission/list',
          title: 'MENU.NEW_ADMISSIONS',
          fontIcon: 'fas fa-user',
        },
        {
          // ESE entry test & interview marks: only while the school has the
          // "Entry test & interview" admission setting on
          to: '/institute/admission/assessment',
          title: 'MENU.ADMISSION_ASSESSMENT',
          fontIcon: 'fas fa-clipboard-check',
          visible:
            generalSettings?.admission_entry_test_enabled === 1 &&
            hasPermission('user_manager_in_active_users'),
        },
        {
          to: '/institute/admission/report',
          title: 'MENU.NEW_ADMS_REPORT',
          fontIcon: 'fas fa-copy',
          visible: false,
        },
        {
          to: '/institute/admission/summary-report',
          title: 'MENU.NEW_ADMS_SUMMARY_REPORT',
          fontIcon: 'fas fa-copy',
          visible: false,
        },

        {
          to: '/institute/admission/settings',
          title: 'MENU.ADMISSION_SETTINGS',
          fontIcon: 'fas fa-wrench',
        },
      ],
    },

    {
      //Enabled Attendance for all clients as per Saqib Zafar
      to: '',
      title: 'MENU.ATTENDANCE',
      fontIcon: 'far fa-edit',
      visible: hasPermission('attendance_manager'),
      subLinks: [
        {
          to: '/institute/attendance/setting/',
          title: 'MENU.ATTENDANCE_SETTING',
          fontIcon: 'fas fa-wrench',
          visible: hasPermission('attendance_manager_settings'),
        },
        {
          to: '/institute/attendance/teacher',
          title: 'MENU.TEACHER/STAFF_ATTENDANCE',
          fontIcon: 'far fa-edit',
          visible:
            hasPermission('attendance_manager_teacher_staff_attendance') &&
            hasPermission('attendance_manager_create'),
        },
        {
          to: '/institute/attendance/teacher-subject',
          title: 'MENU.TEACHER_SUBJECT_ATTENDANCE',
          fontIcon: 'far fa-edit',
          visible:
            hasPermission('attendance_manager_teacher_subject_attendance') &&
            generalSettings?.subject_attendance === GENERAL_ENABLE_DISABLE.ENABLE &&
            hasPermission('attendance_manager_create'),
        },
        {
          to: '/institute/attendance/teacher-report/individual',
          title: 'MENU.TEACHER/STAFF_ATTENDANCE_REPORTS',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_teacher_staff_attendance_report') &&
            hasPermission('attendance_manager_view'),
        },
        {
          to: '/institute/attendance/teacher-subject-report/individual',
          title: 'MENU.TEACHER_SUBJECT_ATTENDANCE_REPORTS',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_teacher_subject_attendance_report') &&
            generalSettings?.subject_attendance === GENERAL_ENABLE_DISABLE.ENABLE &&
            hasPermission('attendance_manager_view'),
        },
        {
          to: '/institute/attendance/student',
          title: 'MENU.STUDENT_ATTENDANCE',
          fontIcon: 'far fa-edit',
          visible:
            hasPermission('attendance_manager_student_attendance') &&
            hasPermission('attendance_manager_create'),
        },
        {
          to: '/institute/attendance/student-subject',
          title: 'MENU.STUDENT_SUBJECT_ATTENDANCE',
          fontIcon: 'far fa-edit',
          visible:
            hasPermission('attendance_manager_student_subject_attendance') &&
            generalSettings?.subject_attendance === GENERAL_ENABLE_DISABLE.ENABLE &&
            hasPermission('attendance_manager_create'),
        },
        {
          to: '/institute/attendance/student-report/individual',
          title: 'MENU.STUDENT_ATTENDANCE_REPORTS',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_student_attendance_report') &&
            hasPermission('attendance_manager_view'),
        },
        {
          to: '/institute/attendance/student-subject-report/individual',
          title: 'MENU.STUDENT_SUBJECT_ATTENDANCE_REPORTS',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_student_subject_attendance_report') &&
            generalSettings?.subject_attendance === GENERAL_ENABLE_DISABLE.ENABLE &&
            hasPermission('attendance_manager_view'),
        },
        {
          to: '/institute/attendance/student-subject/combined-report',
          title: 'MENU.STUDENT_SUBJECT_COMBINED_ATTENDANCE_REPORTS',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_student_subject_attendance_report') &&
            generalSettings?.subject_attendance === GENERAL_ENABLE_DISABLE.ENABLE &&
            hasPermission('attendance_manager_view'),
        },
        // {
        //   to: '/institute/attendanceAmendmentRequest/amendmentRequest',
        //   title: 'ATTENDANCE.MARKED_THROUGH.ATTENDANCE_AMENDMENT_REQUEST',
        //   fontIcon: 'far fa-edit',
        //   visible: hasPermission('attendance_manager_attendance_amendment_requests'),
        // },
        {
          to: '/institute/attendance-custom-schedule/list',
          title: 'MENU.ATTENDANCE_SCHEDULE',
          fontIcon: 'fas fa-clock',
          visible: hasPermission('attendance_manager_attendance_schedule'),
        },
        {
          to: '/institute/attendance/biometricAttendanceUpload/',
          title: 'MENU.ATTENDANCE_UPLOAD',
          fontIcon: 'fas fa-cloud-upload-alt',
          visible: hasPermission('attendance_manager_attendance_upload'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.BRANCHES_&_LEVELS',
      fontIcon: 'fab fa-buromobelexperte',
      visible: hasPermission('branch_level_manager'),
      subLinks: [
        {
          to: '/institute/branches/list',
          title: 'MENU.BRANCHES',
          fontIcon: 'fab fa-buromobelexperte',
          visible: hasPermission('branch_level_manager_branches'),
        },
        {
          to: '/institute/level/list',
          title: 'MENU.LEVELS',
          fontIcon: 'fab fa-buromobelexperte',
          visible: hasPermission('branch_level_manager_levels'),
        },
        {
          to: '/institute/departments',
          title: 'MENU.DEPARTMENTS',
          fontIcon: 'fab fa-buromobelexperte',
          visible: hasPermission('department_manager') && !hideDepartment,
        },
      ],
    },
    {
      to: '',
      title: 'MENU.COUNSELLING',
      fontIcon: 'fas fa-graduation-cap',
      visible: hasPermission('college_counselling'),
      subLinks: [
        {
          to: '/institute/counselling/dashboard',
          title: 'COUNSELLING.ADMIN.TAB.DASHBOARD',
          fontIcon: 'fas fa-gauge',
          visible: hasPermission('college_counselling_dashboard'),
        },
        {
          to: '/institute/counselling/teacher-rec',
          title: 'COUNSELLING.ADMIN.TAB.TEACHER_REC',
          fontIcon: 'fas fa-pen',
          visible: hasPermission('college_counselling_teacher_recommendations'),
        },
        {
          to: '/institute/counselling/settings',
          title: 'COUNSELLING.ADMIN.TAB.SETTINGS',
          fontIcon: 'fas fa-gear',
          visible: hasPermission('college_counselling_settings'),
        },
      ],
    },

    {
      to: '/institute/digital-requests/list',
      title: 'MENU.LEAVE_APPLICATIONS',
      fontIcon: 'fas fa-envelopes-bulk',
      visible: hasPermission('digital_request_manager'),
    },

    {
      to: isTeacher() ? '/institute/news/fullmode' : '/institute/news/list',
      title: 'MENU.NEWS',
      fontIcon: 'far fa-newspaper',
      visible: hasPermission('news_manager'),
    },

    {
      to: '/institute/design-tool',
      title: 'MENU.DESIGN_TOOL',
      fontIcon: 'fas fa-palette',
      visible: isDesignToolEnabled(),
    },

    {
      to: '/institute/resources',
      title: 'MENU.RESOURCES',
      fontIcon: 'fas fa-folder-open',
      visible: hasPermission('resource_manager'),
      subLinks: [
        {
          to: '/institute/resources/list',
          title: 'MENU.RESOURCES',
          fontIcon: 'fas fa-folder-open',
          visible: hasPermission('resource_manager_view'),
        },
        {
          to: '/institute/resource-categories/list',
          title: 'MENU.RESOURCE_CATEGORIES',
          fontIcon: 'fas fa-list',
          visible:
            hasPermission('resource_manager_categories_create') ||
            hasPermission('resource_manager_categories_update') ||
            hasPermission('resource_manager_categories_delete'),
        },
        {
          to: '/institute/resources/archived',
          title: 'MENU.ARCHIVED_RESOURCES',
          fontIcon: 'fas fa-box-archive',
          visible: hasPermission('resource_manager_archive'),
        },
        {
          to: '/institute/resource-archive-folders/list',
          title: 'MENU.RESOURCE_ARCHIVE_FOLDERS',
          fontIcon: 'fas fa-folder-minus',
          visible: hasPermission('resource_manager_archive'),
        },
        {
          to: '/institute/resource-settings',
          title: 'MENU.RESOURCE_SETTINGS',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('resource_manager_settings'),
        },
      ],
    },

    {
      to: '/institute/inquiry',
      title: 'MENU.INQUIRY',
      fontIcon: 'fas fa-question-circle',
      visible: isDevEnv() && hasPermission('inquiry_manager'),

      subLinks: [
        {
          to: '/institute/inquiry/list',
          title: 'MENU.INQUIRY',
          fontIcon: 'fas fa-question-circle',
          visible: isDevEnv() && hasPermission('inquiry_manager_view'),
        },
        {
          to: '/institute/inquiry/inquiry-setting',
          title: 'MENU.INQUIRY_SETTING',
          fontIcon: 'fas fa-cog',
          visible: isDevEnv() && hasPermission('inquiry_manager_update'),
        },
      ],
    },

    // ai4: AI Timetable moved above (below dashboard); else full Schedules submenu
    ...(!(showAi4SimplifiedMenu && !isTeacher())
      ? [
          {
            to: '',
            title: 'MENU.SCHEDULES',
            fontIcon: 'far fa-calendar-alt',
            visible: hasPermission('schedule_manager'),
            subLinks: [
              {
                to: '/institute/schedule/schedule-setting',
                title: 'MENU.SCHEDULES_SETTING',
                fontIcon: 'fas fa-wrench',
                visible: hasPermission('schedule_manager_settings') && !hideScheduleSetting,
              },
              {
                to: '/institute/schedule/yearly-calendar',
                title: 'MENU.YEARLY_SCHEDULE',
                fontIcon: 'far fa-calendar-alt',
                visible: hasPermission('schedule_manager') && !hideAnnualCalendar,
              },
              {
                to: '/institute/schedule/daily-calendar',
                title: 'MENU.DAILY_SCHEDULE',
                fontIcon: 'far fa-calendar-alt',
                visible: hasPermission('schedule_manager') && !hideTimetable,
              },
              {
                to: '/institute/schedule/timetable',
                title: 'MENU.AI_SCHEDULE',
                fontIcon: 'far fa-calendar-alt',
                visible: hasPermission('ai_timetable') && !isTeacher(),
              },
            ],
          },
        ]
      : []),

    {
      to: '',
      title: 'MENU.TAGS',
      fontIcon: 'fas fa-tag',
      visible: hasPermission('tags_manager') && !hideTags,
      subLinks: [
        {
          to: '/institute/tags/list',
          title: 'MENU.TAGS',
          fontIcon: 'fas fa-tag',
          visible: hasPermission('tags_manager') && !hideTags,
        },
        {
          to: '/institute/tag-categories/list',
          title: 'MENU.TAGS_CATEGORY',
          fontIcon: 'fas fa-tag',
        },
      ],
    },

    {
      to: '',
      title: 'MENU.USERS',
      fontIcon: 'fas fa-users',
      visible:
        hasPermission('user_manager') &&
        (hasPermission('user_manager_students') ||
          hasPermission('user_manager_parents') ||
          hasPermission('user_manager_teachers') ||
          hasPermission('us') ||
          hasPermission('user_manager_staff_roles') ||
          hasPermission('user_manager_user_labels') ||
          hasPermission('user_manager_in_active_users') ||
          hasPermission('user_manager_user_letters') ||
          hasPermission('user_manager_settings')),
      subLinks: [
        {
          to: '/institute/student',
          title: 'MENU.STUDENTS',
          fontIcon: 'fas fa-user',
          visible: hasPermission('user_manager_students'),
        },
        {
          to: '/institute/parent',
          title: 'MENU.PARENTS',
          fontIcon: 'fas fa-user',
          visible: hasPermission('user_manager_parents') && !hideParents,
        },
        {
          to: '/institute/teacher',
          title: 'MENU.TEACHERS',
          fontIcon: 'fas fa-user',
          visible: hasPermission('user_manager_teachers'),
        },
        {
          to: '/institute/staff',
          title: 'MENU.STAFF',
          fontIcon: 'fas fa-user',
          visible: hasPermission('user_manager_staffs'),
        },
        {
          to: '/institute/staff-role',
          title: 'MENU.STAFF_ROLES',
          fontIcon: 'fas fa-user',
          visible: hasPermission('user_manager_staff_roles'),
        },
        {
          to: '/institute/label',
          title: 'MENU.LABEL',
          fontIcon: 'fas fa-user',
          visible: hasPermission('user_manager_user_labels') && !hideUserLabel,
        },
        {
          to: '/institute/inactive-users/students',
          // generalSettings?.admission_form_permission === 0
          //   ? '/institute/inactive-users/students'
          //   : '/institute/user/unadmin?tabType=student',
          title: 'MENU.IN-ACTIVE_USERS',
          fontIcon: 'fas fa-user',
          visible: hasPermission('user_manager_in_active_users') && !hideInactiveUsers,
        },
        // {
        //   to: '/institute/generalSettings/admin',
        //   title: 'MENU.SETTINGS',
        //   fontIcon: 'fas fa-wrench',
        //   visible: hasPermission('user_manager_settings'),
        // },
      ],
    },

    /**
     * Academics
     */
    {
      type: 'label' as const,
      title: 'MENU.ACADEMICS',
      to: '',
      visible:
        hasPermission('exam_manager') ||
        hasPermission('activity_manager') ||
        hasPermission('extra_curricular_activities') ||
        hasPermission('homework_manager') ||
        hasPermission('performance_report') ||
        hasPermission('quiz_system') ||
        hasPermission('test_manager') ||
        hasPermission('syllabus_manager') ||
        hasPermission('video_lecture') ||
        (generalSettings.ear_status === 1 && hasPermission('caie_rechecking')),
    },

    {
      to: '',
      title: 'MENU.ACTIVITY_MANAGEMENT',
      fontIcon: 'fa-solid fa-tasks',
      visible: hasPermission('activity_manager'),
      subLinks: [
        {
          to: '/institute/activities/grid',
          title: 'MENU.ACTIVITY_MANAGEMENT',
          fontIcon: 'fas fa-tasks',
        },
        {
          to: '/institute/activityAttendance/grid',
          title: 'Activities Attendance',
          fontIcon: 'fas fa-edit',
        },
        {
          to: '/institute/activityAttendance/studentReportActivity',
          title: 'Activities Attendance Report',
          fontIcon: 'fas fa-file',
        },
        {
          to: '/institute/activitySettings/grid',
          title: 'Activity Settings',
          fontIcon: 'fas fa-cog',
        },
      ],
    },

    {
      to: '/institute/studentRecheckingRecord/admin',
      title: 'CAIE Rechecking',
      fontIcon: 'fa-solid fa-check-to-slot',
      visible: generalSettings.ear_status === 1 && hasPermission('caie_rechecking'),
    },

    {
      to: '',
      title: 'MENU.CLASSES_&_SUBJECTS',
      fontIcon: 'fas fa-cog',
      visible: hasPermission('class_subject_manager') && !(showAi4SimplifiedMenu && !isTeacher()),
      subLinks: [
        {
          to: '/institute/grades',
          title: 'MENU.GRADES',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('class_subject_manager_grades'),
        },
        {
          to: '/institute/sections',
          title: 'MENU.SECTIONS',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('class_subject_manager_sections'),
        },
        {
          to: '/institute/classes',
          title: 'MENU.CLASS',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('class_subject_manager_classes'),
        },
        {
          to: '/institute/subjects',
          title: 'MENU.SUBJECT',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('class_subject_manager_subjects'),
        },
      ],
    },

    // The NEW exam manager (React/Node conversion), running in PARALLEL with
    // the legacy iframe module below (Saqib, 2026-08-20). Gated on
    // isNewExamManagerEnabled(): a dev build, or the tenant's
    // general_settings.new_exam_manager flag AND the super admin exam feature
    // (which rides inside hasPermission('exam_manager')). The legacy group
    // stays exactly as it is either way.
    {
      to: '',
      title: customTitle('MENU.EXAM_MANAGER_BETA'),
      fontIcon: 'fas fa-flask',
      visible: isNewExamManagerEnabled() && hasPermission('exam_manager'),
      // The "New" badge marks a screen the school did not have at all — no
      // legacy page, no legacy tab. Weightages and Grace Marks qualify because
      // the legacy has no editor for either: exam-type weightages are written
      // as a side effect of creating an exam (`ExamController:1708`, `:2081`,
      // `:712`) and grace is a column inside Multi-Exam Marking (`:1083`).
      // Everything else below replaces a legacy screen and stays unbadged,
      // Academic Records included -- the legacy edits those on the student
      // form (`views/school/user/_previous_exam_form.php`).
      subLinks: [
        {
          // Dev-only onboarding for the new module: the setup checklist and
          // guided-tour entry. Gated on isDevEnv() — never visible on prod.
          to: '/institute/exam/getting-started',
          title: 'MENU.EXAM_GETTING_STARTED',
          fontIcon: 'fas fa-rocket',
          badgeLabel: 'MENU.BADGE.NEW',
          badgeColor: 'light-primary',
          visible: isDevEnv() && hasPermission('exam_manager'),
        },
        {
          to: '/institute/exam/admin',
          title: 'MENU.EXAM',
          fontIcon: 'fas fa-file',
          visible: hasPermission('exam_manager_exams'),
        },
        {
          to: '/institute/exam/multi-exam',
          title: 'MENU.MULTI_EXAM_MARKING',
          fontIcon: 'fas fa-table',
          visible: hasPermission('exam_manager_exams'),
        },
        {
          to: '/institute/exam/weightages',
          title: 'MENU.EXAM_WEIGHTAGES',
          fontIcon: 'fas fa-balance-scale',
          badgeLabel: 'MENU.BADGE.NEW',
          badgeColor: 'light-primary',
          visible: hasPermission('exam_manager_exams'),
        },
        {
          to: '/institute/exam/grace-marks',
          title: 'MENU.GRACE_MARKS',
          fontIcon: 'fas fa-hand-holding-heart',
          badgeLabel: 'MENU.BADGE.NEW',
          badgeColor: 'light-primary',
          // A school that switched grace off should not be offered a screen for
          // it. The legacy has no standalone grace editor at all — grace is a
          // column inside multi-exam marking, and it drops that column on this
          // same key (`multi_exam_mark_new_2.php:7-12`). Defaults to visible,
          // as the legacy's `isset` check does, so a tenant without the key set
          // keeps today's behaviour. The route enforces it too.
          visible:
            hasPermission('exam_manager_exams') && generalSettings?.grace_marks_visibility !== 0,
        },
        {
          to: '/institute/exam/remarks',
          title: 'MENU.EXAM_REMARKS',
          fontIcon: 'fas fa-comment-dots',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/institute/exam/report-cards',
          // Same label as the legacy entry on purpose: this IS that screen,
          // and the school knows it by that name.
          title: 'MENU.NEW_EXAM_REPORT_CARD',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/institute/exam/report-cards/generate',
          title: 'MENU.REPORT_CARD.GENERATION',
          fontIcon: 'fas fa-cogs',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/institute/exam/report-card-settings',
          title: 'MENU.EXAM_REPORT_CARD_SETTINGS',
          fontIcon: 'fas fa-sliders-h',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/institute/exam/report-card-settings/extra-parameters',
          title: 'MENU.REPORT_CARD.EXTRA_PARAMETERS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/institute/exam/academic-records',
          title: 'MENU.ACADEMIC_RECORDS',
          fontIcon: 'fas fa-history',
          visible: hasPermission('user_manager_students'),
        },
        {
          to: '/institute/exam/reports',
          title: 'MENU.EXAM_REPORT.PDF_REPORTS',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_pdf_reports'),
        },
        {
          to: isDevEnv() ? '/institute/exam-type' : '/institute/examType/admin',
          title: 'MENU.EXAM_Type',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('exam_manager_exam_types'),
        },
        {
          to: isDevEnv() ? '/institute/examtypelabels' : '/institute/examtypeLabels/admin',
          title: 'MENU.EXAM_TYPE_LABEL',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('exam_manager_exam_type_labels'),
        },
        {
          to: '/institute/exam/skill-reports',
          title: 'MENU.SKILL_REPORTS',
          fontIcon: 'fas fa-star-half-alt',
          visible: hasPermission('class_subject_manager_view'),
        },
        {
          to: '/institute/exam/caie-rechecking',
          title: 'MENU.CAIE_RECHECKING',
          fontIcon: 'fas fa-redo',
          visible: hasPermission('caie_rechecking_view'),
        },
        {
          to: isDevEnv()
            ? isTeacher()
              ? '/institute/exam-schedule'
              : '/institute/exam-schedule/settings'
            : '/institute/exam/datesheet',
          title: 'MENU.EXAM_SCHEDULE',
          fontIcon: 'fas fa-calendar',
          visible: hasPermission('exam_manager_exam_schedules'),
        },
        {
          to: '/institute/exam/consolidated-report',
          title: 'MENU.EXAM_CONSOLIDATED_REPORT',
          fontIcon: 'fas fa-file-pdf',
          visible: hasPermission('apr_reports'),
        },
        {
          to: '/institute/exam/exam-comments',
          title: 'MENU.EXAM_COMMENTS',
          fontIcon: 'fas fa-comment-dots',
          visible:
            hasPermission('exam_manager') && (isLyceum() || isLyceumTest() || isDemocollege()),
        },
        {
          to: '/institute/exam/report',
          title: 'MENU.LEGACY_EXAM_REPORT_CARD',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('exam_manager_exam_report_cards') &&
            [
              'aha',
              'aligarh',
              'dam',
              'dcc',
              'dssk',
              'ha',
              'ibaccdadu',
              'ibakhp',
              'ibapsl',
              'ims',
              'kss',
              'leadership',
              'maniyas',
              'maryammont',
              'psi',
              'psm',
              'sgs',
              'wits',
            ].includes(getTenantName()),
        },
        {
          to: isDevEnv()
            ? '/institute/exam-report-card-word-templates'
            : '/institute/WordReportcardTemplates/admin',
          title: 'MENU.EXAM_REPORTCARD_WORD_TEMPLATES',
          fontIcon: 'fas fa-file-word',
          visible: hasPermission('exam_manager_report_template'),
        },
      ],
    },

    {
      to: '',
      title: customTitle('MENU.EXAMS'),
      fontIcon: 'fas fa-copy',
      visible: (isDevEnv() || !isNewExamManagerEnabled()) && hasPermission('exam_manager'),
      subLinks: [
        {
          // to: `/institute/${generalSettings?.use_gradewise_term ? 'gradewiseTerm' : 'term'}/admin`,
          to: isDevEnv()
            ? '/institute/exam/term'
            : `/institute/${generalSettings?.use_gradewise_term ? 'gradewiseTerm' : 'term'}/admin`,
          title: 'MENU.TERMS',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('exam_manager_terms') && !isTeacher(),
        },
        {
          to: generalSettings?.use_new_grading_key
            ? '/institute/exam/grading-key'
            : `/institute/examGrade/${generalSettings?.grading_key ? 'labelWiseAdmin' : 'admin'}`,
          title: 'MENU.EXAM_GRADE',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('exam_manager_exam_grades'),
        },
        {
          to: isDevEnv() ? '/institute/exam-type' : '/institute/examType/admin',
          title: 'MENU.EXAM_Type',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('exam_manager_exam_types'),
        },
        {
          to: isDevEnv() ? '/institute/examtypelabels' : '/institute/examtypeLabels/admin',
          title: 'MENU.EXAM_TYPE_LABEL',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('exam_manager_exam_type_labels'),
        },
        {
          to: '/institute/exam/admin',
          title: 'MENU.EXAM',
          fontIcon: 'fas fa-file',
          visible: hasPermission('exam_manager_exams'),
        },
        {
          to: '/institute/examReportcardSetting/index',
          title: 'MENU.EXAM_REPORT_CARD_SETTINGS',
          fontIcon: 'fas fa-wrench',
          visible: hasPermission('exam_manager_exam_report_card_settings'),
        },
        {
          to: '/institute/reportcardResult/admin',
          title: 'MENU.NEW_EXAM_REPORT_CARD',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/institute/examReportcardSetting/examScheduleSetting',
          title: 'MENU.EXAM_SHECHDULE_SETTINGS',
          fontIcon: 'far fa-calendar-alt',
          visible: hasPermission('exam_manager_exam_schedule_settings'),
        },
        {
          to: isDevEnv()
            ? isTeacher()
              ? '/institute/exam-schedule'
              : '/institute/exam-schedule/settings'
            : '/institute/exam/datesheet',
          title: 'MENU.EXAM_SCHEDULE',
          fontIcon: 'fas fa-calendar',
          visible: hasPermission('exam_manager_exam_schedules'),
        },
        {
          to: '/institute/exam/consolidated-report',
          title: 'MENU.EXAM_CONSOLIDATED_REPORT',
          fontIcon: 'fas fa-file-pdf',
          visible: hasPermission('apr_reports'),
        },
        {
          to: '/institute/exam/exam-comments',
          title: 'MENU.EXAM_COMMENTS',
          fontIcon: 'fas fa-comment-dots',
          visible:
            hasPermission('exam_manager') && (isLyceum() || isLyceumTest() || isDemocollege()),
        },
        {
          to: '/institute/exam/report',
          title: 'MENU.LEGACY_EXAM_REPORT_CARD',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('exam_manager_exam_report_cards') &&
            [
              'aha',
              'aligarh',
              'dam',
              'dcc',
              'dssk',
              'ha',
              'ibaccdadu',
              'ibakhp',
              'ibapsl',
              'ims',
              'kss',
              'leadership',
              'maniyas',
              'maryammont',
              'psi',
              'psm',
              'sgs',
              'wits',
            ].includes(getTenantName()),
        },
        {
          to: isDevEnv()
            ? '/institute/exam-report-card-word-templates'
            : '/institute/WordReportcardTemplates/admin',
          title: 'MENU.EXAM_REPORTCARD_WORD_TEMPLATES',
          fontIcon: 'fas fa-file-word',
          visible: hasPermission('exam_manager_report_template'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.EX-CURRICULAR_ACTIVITIES',
      fontIcon: 'fas fa-layer-group',
      visible: hasPermission('extra_curricular_activities'),
      subLinks: [
        // {
        //   to: '/institute/site/stream',
        //   title: 'MENU.LIVE_STREAMING',
        //   fontIcon: 'fas fa-bolt',
        //   visible: hasPermission('extra_curricular_activities_live_streaming'),
        // },
        {
          to: '/institute/events/highlights',
          title: 'MENU.EVENTS',
          fontIcon: 'la la-photo',
          visible: hasPermission('extra_curricular_activities_photo_gallery'),
        },
        {
          to: '/institute/sports/list',
          title: 'MENU.SPORTS',
          fontIcon: 'fas fa-layer-group',
          visible: !isTeacher(),
        },
      ],
    },

    {
      //Enabled Homework for all clients as per Saqib Zafar
      to: '/institute/homeworks',
      title: customTitle('MENU.HOMEWORKS'),
      fontIcon: 'fas fa-book-open',
      visible: hasPermission('homework_manager'),
    },

    {
      to: '',
      title: 'MENU.PERFORMANCE_REPORTS',
      fontIcon: 'far fa-copy',
      visible: hasPermission('performance_report'),
      subLinks: [
        {
          to: '/institute/reports/setting',
          title: 'MENU.REPORT_SETTING',
          fontIcon: 'fas fa-wrench',
          visible: hasPermission('performance_report_settings'),
        },
        {
          to: '/institute/reports/homework',
          title: 'MENU.HOMEWORK_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_homework_reports'),
        },
        {
          to: '/institute/reports/test',
          title: 'MENU.TEST_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_test_reports'),
        },

        {
          to: '/institute/reports/exam',
          title: 'MENU.EXAM_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_exam_reports'),
        },

        {
          to: '/institute/reports/overall',
          title: 'MENU.OVERALL_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_overall_reports'),
        },
        {
          to: '/institute/performance-reports',
          title: 'MENU.ALL_REPORTS',
          fontIcon: 'far fa-copy',
          visible: isDevEnv() && hasPermission('performance_report_overall_reports'),
        },
        {
          to: '/institute/reports/pdfreports',
          title: 'MENU.PDF_REPORTS',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_pdf_reports'),
        },
        {
          // one page: native Comprehensive / CAIE Statistics tabs plus the legacy
          // Previous Grades, Consolidated and Predicted Grades reports as iframe tabs
          to: '/institute/performance-reports/oa-level-reports',
          title: 'MENU.O/A_LEVEL_REPORTS',
          fontIcon: 'far fa-copy',
          visible:
            hasPermission('performance_report_oa_level_reports') ||
            (isTeacher() && hasPermission('performance_report_oa_level_comprehensive_reports')),
        },
        {
          to: '/institute/performance-reports/exam',
          title: 'PERFORMANCE_REPORTS.EXAM_CONSOLIDATE_REPORT.TITLE',
          fontIcon: 'far fa-copy',
          // visible: hasPermission('performance_report_pdf_reports'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.QUIZ_SYSTEM',
      fontIcon: 'far fa-file',
      visible: hasPermission('quiz_system'),
      subLinks: [
        {
          to: '/institute/quizsystem/quizDifficultylevel/admin',
          title: 'MENU.DIFFICULTY_LEVEL',
          fontIcon: 'fas fa-list',
          visible: hasPermission('quiz_system_difficulty_level'),
        },
        {
          to: '/institute/quizsystem/quizLabel/admin',
          title: 'MENU.LABEL',
          fontIcon: 'fas fa-list',
          visible: hasPermission('quiz_system_label'),
        },
        {
          to: '/institute/quizsystem/quizQuestion/admin',
          title: 'MENU.QUESTION',
          fontIcon: 'fas fa-list',
          visible: hasPermission('quiz_system_question'),
        },
        {
          to: '/institute/quizsystem/quizsystem/settings',
          title: 'MENU.SETTINGS',
          fontIcon: 'fas fa-wrench',
          visible: hasPermission('quiz_system_settings'),
        },
      ],
    },

    {
      to: '/institute/tests',
      title: customTitle('MENU.TESTS'),
      fontIcon: 'fas fa-address-book',
      visible: hasPermission('test_manager'),
    },

    {
      to: '/institute/syllabus',
      title: 'MENU.SYLLABUS_MANAGER',
      fontIcon: 'far fa-edit',
      visible: hasPermission('syllabus_manager'),
    },

    {
      to: '',
      title: 'MENU.VIDEO_LECTURES',
      fontIcon: 'fas fa-film',
      visible: hasPermission('video_lecture'),
      subLinks: [
        {
          to: '/institute/lectures-new/list?type=0',
          // to: '/institute/lectures-new/list?type=0',
          title: 'MENU.RECORDED_LECTURES',
          fontIcon: 'fas fa-film',
        },
        {
          to: '/institute/lectures-new/list?type=1',
          // to: '/institute/lectures-new/list?type=1',
          title: 'MENU.LIVE_LECTURES',
          fontIcon: 'fas fa-camera-retro',
        },
      ],
    },

    /**
     * Finance
     */
    {
      type: 'label' as const,
      title: 'MENU.FINANCE',
      to: '',
      visible:
        hasPermission('account_manager') ||
        hasPermission('fee_manager') ||
        hasPermission('finance_manager') ||
        hasPermission('hr_manager') ||
        hasPermission('royalty_manager') ||
        hasPermission('vendors_module'),
    },

    {
      to: '',
      title: 'MENU.ACCOUNTS',
      fontIcon: 'fas fa-briefcase',
      visible: hasPermission('account_manager'),
      subLinks: [
        {
          to: '/institute/am/default/settings',
          title: 'MENU.ACCOUNTS_SETTINGS',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('account_manager_settings'),
        },

        {
          to: isDevEnv() ? '/institute/accountType' : '/institute/am/accountType/admin',
          title: 'MENU.ACCOUNT_TYPES',
          fontIcon: 'fas fa-list',
          visible: hasPermission('account_manager_types'),
        },
        {
          to: '/institute/am/account/admin',
          title: 'MENU.CHART_OF_ACCOUNTS',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('account_manager_chart_of_accounts'),
        },
        // {
        //   to: '/institute/am/actmVendor/admin',
        //   title: 'MENU.VENDORS',
        //   fontIcon: 'fas fa-users',
        //   visible: hasPermission('account_manager_vendors'),
        // },
        {
          to: '/institute/am/transaction/billIndex',
          title: customTitle('MENU.PAYMENT_VOUCHERS'),
          fontIcon: 'far fa-edit',
          visible: hasPermission('account_manager_payment_vouchers'),
        },
        {
          to: '/institute/am/transaction/bpvIndex',
          title: customTitle('MENU.BANK_PAYMENT_VOUCHERS'),
          fontIcon: 'far fa-edit',
        },
        {
          to: '/institute/am/transaction/cpvIndex',
          title: customTitle('MENU.CASH_PAYMENT_VOUCHERS'),
          fontIcon: 'far fa-edit',
        },
        {
          to: '/institute/am/transaction/invoiceIndex',
          title: customTitle('MENU.RECEIPT_VOUCHERS'),
          fontIcon: 'far fa-edit',
          visible: hasPermission('account_manager_receipt_vouchers'),
        },
        {
          to: '/institute/am/transaction/pettyCashIndex',
          title: 'MENU.PETTY_CASH_VOUCHERS',
          fontIcon: 'far fa-edit',
          visible: hasPermission('account_manager_petty_cash_vouchers'),
        },
        {
          to: '/institute/am/manualJournal/index',
          title: 'MENU.MANUAL_JOURNALS',
          fontIcon: 'far fa-edit',
          visible: hasPermission('account_manager_manual_journals'),
        },
        {
          to: '',
          title: 'MENU.REPORTS',
          fontIcon: 'fas fa-copy',
          subLinks: [
            {
              to: '/institute/am/profitLoss/statementRaw',
              title: 'MENU.PROFIT_&_LOSS',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/balanceSheet/statementRaw',
              title: 'MENU.BALANCE_SHEET',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/generalLedger/report?method=raw',
              title: 'MENU.GENERAL_LEDGER',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/trialBalance/report',
              title: 'MENU.TRIAL_BALANCE',
              fontIcon: 'fas fa-copy',
            },
            // {
            //   to: '/institute/am/trialBalance/report?new=true',
            //   title: 'MENU.TRIAL_BALANCE_NEW',
            //   fontIcon: 'fas fa-copy',
            // },
            {
              to: '/institute/am/accountDetail/reportRaw',
              title: 'MENU.DETAILED_ACCOUNTS_REPORT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/reconciliationReport/report',
              title: 'MENU.BANK_RECONCILIATION_STATEMENT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/agingReport/report',
              title: 'MENU.AGING_REPORT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/ledgerReport/report',
              title: 'MENU.LEDGER_REPORT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/vendorLedger/report',
              title: 'MENU.VENDOR_LEDGER_REPORT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/newReconciliation/report',
              title: 'MENU.NEW_BANK_RECONCILIATION_REPORT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/receiptReport/report',
              title: 'MENU.RECEIPT_REPORT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/financialReport/statement',
              title: 'MENU.FINANCIAL_STATEMENT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/classwiseAccountDetail/reportRaw',
              title: 'MENU.CLASSWISE_ACCOUNT_DETAILS-REPORT',
              fontIcon: 'fas fa-copy',
            },
            {
              to: '/institute/am/studentFeeLedger/report',
              title: 'MENU.STUDENT_FEE_LEDGER',
              fontIcon: 'fas fa-copy',
            },
          ],
        },
        {
          to: '/institute/am/accountRecordLog/admin',
          title: 'MENU.ACCOUNTS_DETAIL_LOG',
          fontIcon: 'far fa-file',
          visible: hasPermission('account_manager_detail_logs'),
        },
        {
          to: '/institute/am/actmReportConfig/admin',
          title: 'MENU.CUSTOM_REPORTS',
          fontIcon: 'fas fa-gear',
        },
        {
          to: '/institute/monthly-audit-summary',
          title: 'FEE.AUDIT',
          fontIcon: 'fas fa-magnifying-glass-chart',
          visible: isDevEnv(),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.FEES',
      fontIcon: 'fas fa-wallet',
      visible: hasPermission('fee_manager'),
      subLinks: [
        {
          to: '/institute/finance/feeParticular/admin',
          title: 'MENU.FEE_PARTICULAR',
          fontIcon: 'fas fa-list',
          visible: hasPermission('fee_manager_fee_particulars'),
        },
        {
          to: '/institute/finance/feeDiscount/admin',
          title: 'MENU.FEE_DISCOUNT',
          fontIcon: 'fas fa-list',
          visible: hasPermission('fee_manager_fee'),
        },
        {
          to: '/institute/finance/feeCollection/admin',
          title: 'MENU.FEE_COLLECTION',
          fontIcon: 'fas fa-list',
          visible: hasPermission('fee_manager_fee_collection'),
        },
        {
          to: '/institute/admission-vouchers',
          title: 'MENU.ADMISSION_VOUCHERS',
          fontIcon: 'fas fa-file-invoice-dollar',
          visible: hasPermission('fee_manager_admission_vouchers'),
        },
        {
          to: '/institute/finance/feeCollection/reports',
          title: 'MENU.REPORTS',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('fee_manager_defaulter_reports'),
        },
        {
          to: '/institute/finance/feeSettings/settings',
          title: 'MENU.FEE_SETTINGS',
          fontIcon: 'fas fa-wrench',
          visible: hasPermission('fee_manager_settings'),
        },
        {
          to: '/institute/finance/bankAccount/admin',
          title: 'MENU.BANK_ACCOUNTS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('fee_manager_bank_accounts'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.FINANCES',
      fontIcon: 'fas fa-briefcase',
      visible: hasPermission('finance_manager'),
      subLinks: [
        {
          to: '/institute/finance/assetCategory/admin',
          title: 'MENU.ASSET_CATEGORY',
          fontIcon: 'fas fa-list',
          visible: hasPermission('finance_manager_asset_categories'),
        },
        {
          to: '/institute/finance/financeAsset/admin',
          title: 'MENU.ASSETS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('finance_manager_assets'),
        },
        {
          to: '/institute/finance/expenseCategory/admin',
          title: 'MENU.EXPENSE_CATEGORY',
          fontIcon: 'fas fa-list',
          visible: hasPermission('finance_manager_expense_categories'),
        },
        {
          to: '/institute/finance/financeExpense/admin',
          title: 'MENU.EXPENSES',
          fontIcon: 'fas fa-list',
          visible: hasPermission('finance_manager_expenses'),
        },
        {
          to: '/institute/finance/finance/incomereport',
          title: 'MENU.INCOME/REVENUE',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('finance_manager_income_revenue'),
        },
        {
          to: '/institute/finance/finance/incomeexpensereport',
          title: 'MENU.INCOME_AND_EXPENSE_REPORT',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('finance_manager_income_expense_report'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.HUMAN_RESOURCES',
      fontIcon: 'fas fa-user-plus',
      visible: hasPermission('hr_manager'),
      subLinks: [
        {
          to: '/institute/hr/hrPayroll/admin',
          title: 'MENU.PAYROLL',
          fontIcon: 'fas fa-list',
          visible: hasPermission('hr_manager_payroll'),
        },
        {
          to: '/institute/hr/hr/employees',
          title: 'MENU.EMPLOYEES',
          fontIcon: 'fas fa-list',
          visible: hasPermission('hr_manager_employees'),
        },
        {
          to: '/institute/hr/hr/salaryrecord',
          title: 'MENU.SALARY_RECORD',
          fontIcon: 'fas fa-list',
          visible: hasPermission('hr_manager_salary_records'),
        },
        {
          to: '/institute/hr/hr/settings',
          title: 'MENU.HR_SETTINGS',
          fontIcon: 'fas fa-wrench',
          visible: hasPermission('hr_manager_settings'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.ROYALTIES',
      fontIcon: 'fas fa-briefcase',
      visible: hasPermission('royalty_manager'),
      subLinks: [
        {
          to:
            // isDevEnv() || ['tres'].includes(getTenantName())
            // ?
            '/institute/royalty',
          // : '/institute/royality/royalityInvoice/create',
          title: 'MENU.ROYALTY_INVOICE',
          fontIcon: 'fas fa-list',
          visible: hasPermission('royalty_manager'),
        },
        {
          to: '/institute/royalty/settings',
          title: 'MENU.SETTINGS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('royalty_manager_update'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.VENDORS',
      fontIcon: 'fas fa-users',
      visible: hasPermission('vendors_module'),
      subLinks: [
        {
          to: '/institute/vendors',
          title: 'MENU.VENDORS',
          fontIcon: 'fas fa-users',
        },
        {
          to: '/institute/vendors/add',
          title: 'MENU.ADD_VENDOR',
          fontIcon: 'fas fa-users',
          visible: hasPermission('vendors_module_create'),
        },
        {
          to: '/institute/vendors/settings',
          title: 'MENU.VENDOR_SETTINGS',
          fontIcon: 'fas fa-users',
          visible: hasPermission('vendors_module_settings'),
        },
      ],
    },

    /**
     * Management
     */
    {
      type: 'label' as const,
      title: 'MENU.MANAGEMENT',
      to: '',
      visible:
        hasPermission('blog_manager') ||
        hasPermission('inventory_manager') ||
        hasPermission('user_manager_user_letters') ||
        hasPermission('library_module') ||
        hasPermission('notification_manager') ||
        hasPermission('request_manager') ||
        hasPermission('cms_module') ||
        hasPermission('observation_manager'),
    },

    {
      to: '',
      title: 'MENU.FORMS',
      fontIcon: 'fas fa-file-alt',
      visible: hasPermission('form_manager'),
      subLinks: [
        {
          to: '/institute/forms',
          title: 'MENU.FORMS',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('form_manager'),
        },
        {
          to: '/institute/questions',
          title: 'MENU.QUESTIONS',
          fontIcon: 'fas fa-circle-question',
          visible: hasPermission('form_question_manager'),
        },
        {
          to: '/institute/forms/observation-settings',
          title: 'MENU.SETTINGS',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('form_manager_update'),
        },
      ],
    },

    {
      to: '/institute/observations',
      title: 'MENU.OBSERVATION_HEADING',
      fontIcon: 'fas fa-edit',
      visible: hasPermission('observation_manager'),
    },

    // {
    //   to: '',
    //   title: 'MENU.BLOGS',
    //   fontIcon: 'far fa-comment-dots',
    //   visible: hasPermission('blog_manager'),
    //   subLinks: [
    //     {
    //       to: '/institute/schoolblog/blogCategory/admin',
    //       title: 'MENU.CATEGORY',
    //       fontIcon: 'fas fa-cog',
    //       visible: hasPermission('blog_manager_categories'),
    //     },
    //     {
    //       to: '/institute/schoolblog/blogPosts/admin',
    //       title: 'MENU.POSTS',
    //       fontIcon: 'far fa-copy',
    //       visible: hasPermission('blog_manager_posts'),
    //     },
    //     {
    //       to: '/institute/schoolblog/blogComments/admin',
    //       title: 'MENU.COMMENTS',
    //       fontIcon: 'fas fa-comments',
    //       visible: hasPermission('blog_manager_comments'),
    //     },
    //     {
    //       to: '/institute/schoolblog/bannedwords/admin',
    //       title: 'MENU.BANNED_WORDS',
    //       fontIcon: 'fas fa-ban',
    //       visible: hasPermission('blog_manager_banned_words'),
    //     },
    //     {
    //       to: '/institute/schoolblog/blogs',
    //       title: 'MENU.BLOG_FRONT_VIEW',
    //       fontIcon: 'fas fa-comment-dots',
    //       visible: hasPermission('blog_manager_blogs'),
    //     },
    //   ],
    // },

    {
      to: '',
      title: 'MENU.INVENTORY',
      fontIcon: 'fas fa-list',
      visible: hasPermission('inventory_manager'),
      subLinks: [
        {
          to: '/institute/inventory/dashboard',
          title: 'MENU.DASHBOARD',
          fontIcon: 'fas fa-list',
          visible:
            (isDevEnv() || isDemocollege()) && hasPermission('inventory_manager_item_categories'),
        },
        {
          to: '/institute/inventory/stock-ledger',
          title: 'MENU.STOCKS_MOVEMENT',
          fontIcon: 'fas fa-list',
          visible:
            (isDevEnv() || isDemocollege()) && hasPermission('inventory_manager_item_categories'),
        },
        {
          to:
            isDevEnv() || isDemocollege()
              ? '/institute/inventory/item-categories/list'
              : '/institute/inventory/itemCategory/admin',
          title: 'MENU.ITEM_CATEGORIES',
          fontIcon: 'fas fa-list',
          visible: hasPermission('inventory_manager_item_categories'),
        },
        {
          to:
            isDevEnv() || isDemocollege()
              ? '/institute/inventory/store-items/list'
              : '/institute/inventory/storeItem/admin',
          title: 'MENU.STORE_ITEMS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('inventory_manager_store_items'),
        },
        {
          to:
            isDevEnv() || isDemocollege()
              ? '/institute/inventory/invoices/list'
              : '/institute/inventory/invoice/admin',
          title: generalSettings?.invoice_button_name
            ? generalSettings.invoice_button_name
            : 'MENU.INVOICES',
          fontIcon: 'fas fa-list',
          visible: hasPermission('inventory_manager_invoices'),
        },
        {
          to:
            isDevEnv() || isDemocollege()
              ? '/institute/inventory/purchase-orders/list'
              : '/institute/inventory/purchaseOrder/admin',
          title: 'MENU.PURCHASE_ORDERS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('inventory_manager_purchase_orders'),
        },
        {
          to:
            isDevEnv() || isDemocollege()
              ? '/institute/inventory/goods-receipts/list'
              : '/institute/inventory/goodsReceipt/admin',
          title: 'MENU.GOODS_RECEIPTS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('inventory_manager_goods_receipts'),
        },
        {
          to:
            isDevEnv() || isDemocollege()
              ? '/institute/inventory/reports/stock-report'
              : '/institute/inventory/report/reportIndex',
          title: 'MENU.REPORTS',
          fontIcon: 'fas fa-list',
          visible: hasPermission('inventory_manager_reports') && !isStaff(),
        },
        {
          to:
            isDevEnv() || isDemocollege()
              ? '/institute/inventory/inventory-settings'
              : '/institute/inventory/inventorySettings/update',
          title: 'SETTINGS_CATEGORY.INVENTORY_SETTINGS',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('inventory_manager_reports') && !isStaff(),
        },
      ],
    },

    {
      to: '/institute/certificates/list',
      title: 'MENU.LETTERS_AND_CERTIFICATES',
      fontIcon: 'fas fa-file',
      visible: hasPermission('user_manager_user_letters'),
    },

    {
      to: '',
      title: 'MENU.LIBRARY',
      fontIcon: 'fas fa-university',
      visible: hasPermission('library_module') && !isTeacher(),
      subLinks: [
        {
          to: '/institute/library/categories/list',
          title: 'MENU.LIBRARY_CATEGORY',
          fontIcon: 'fas fa-list-alt',
          visible: hasPermission('library_module_categories'),
        },
        {
          to: '/institute/library/subjects/list',
          title: 'MENU.LIBRARY_SUBJECT',
          fontIcon: 'fas fa-address-card',
          visible: hasPermission('library_module_subjects'),
        },
        {
          to: '/institute/library/books/list',
          title: 'MENU.LIBRARY_BOOK',
          fontIcon: 'fas fa-book',
          visible: hasPermission('library_module_books'),
        },
        {
          to: '/institute/library/book-reservations/list',
          title: 'MENU.BOOK_RESERVATION',
          fontIcon: 'fas fa-calendar-alt',
          visible: hasPermission('library_module_book_issuance'),
        },
        {
          to: '/institute/library/settings',
          title: 'MENU.BOOK_SETTING',
          fontIcon: 'fas fa-cog',
          visible: hasPermission('library_module_settings'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.NOTIFICATIONS',
      fontIcon: 'fa fa-bell',
      visible: hasPermission('notification_manager'),
      subLinks: [
        {
          to: '/institute/notification-settings',
          title: 'MENU.NOTIFICATIONS_SETTING',
          fontIcon: 'fa fa-bell',
          visible: hasPermission('notification_manager_settings'),
        },

        {
          to: '/institute/email-settings',
          title: 'MENU.EMAIL_SETTING',
          fontIcon: 'fa fa-envelope',
          visible: hasPermission('notification_manager_email_settings'),
        },
        {
          to: '/institute/notifications/admin',
          fontIcon: 'far fa-bell',
          title: 'MENU.NOTIFICATIONS',
          visible: hasPermission('notification_manager_notifications'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.STAFF_REQUESTS',
      fontIcon: 'fas fa-envelopes-bulk',
      visible: hasPermission('request_manager'),
      subLinks: [
        {
          to: '/institute/requests/list',
          title: 'MENU.REQUESTS',
          fontIcon: 'fas fa-file-pen',
        },
        {
          to: '/institute/requests/settings',
          title: 'MENU.REQUEST_SETTINGS',
          fontIcon: 'fas fa-gear',
          visible: hasPermission('request_manager_settings'),
        },
      ],
    },

    // {
    //   to: '',
    //   title: 'MENU.WEBSITE',
    //   fontIcon: 'fas fa-list',
    //   visible: hasPermission('cms_module'),
    //   subLinks: [
    //     {
    //       to: '/institute/cms/cmsControlls/admin',
    //       title: 'MENU.WEBSITE_SETTINGS',
    //       fontIcon: 'fas fa-cog',
    //       visible: hasPermission('cms_module_settings'),
    //     },
    //     {
    //       to: '/institute/cms/cmsPages/admin',
    //       title: 'MENU.PAGES',
    //       fontIcon: 'fas fa-list',
    //       visible: hasPermission('cms_module_pages'),
    //     },
    //   ],
    // },

    /**
     * Other (hidden when isSelectiveAiDomains - CSV Import is in Data menu)
     */
    {
      type: 'label' as const,
      title: 'MENU.OTHER',
      to: '',
      visible:
        hasPermission('data_upload_manager') || hasPermission('system_reports') || isMyschool(),
    },

    // ai4: CSV Import is inside Data menu; else full Data Import submenu

    {
      to: '',
      title: 'MENU.DATA_IMPORT',
      fontIcon: 'fas fa-cloud-upload-alt',
      visible: hasPermission('data_upload_manager'),
      subLinks: [
        {
          to: '/institute/data-import/class-subject-teacher/upload',
          title: 'MENU.CLASS/SUBJECT/TEACHER',
          fontIcon: 'fas fa-cogs',
          visible:
            hasPermission('data_upload_manager_class_subject_teachers') &&
            !hideImportClassSubjectTeacher,
        },
        {
          to: '/institute/data-import/student-parent/upload',
          title: 'MENU.STUDENT/PARENT',
          fontIcon: 'fas fa-users',
          visible: hasPermission('data_upload_manager_student_parents') && !hideImportStudentParent,
        },
        {
          // to: '/institute/data-import/user-labels/upload',
          to: '/institute/dataupload/default/ulAdmin',
          title: 'MENU.USER_LABEL',
          fontIcon: 'fas fa-tags',
          visible: !hideImportUserLabel,
        },
        {
          to: '/institute/dataupload/default/dsAdmin',
          title: 'MENU.DAILY_SCHEDULE',
          fontIcon: 'fas fa-calendar',
          visible:
            hasPermission('schedule_manager') &&
            hasPermission('data_upload_manager_daily_schedules') &&
            !hideImportTimetable,
        },
        {
          to: '/institute/dataupload/default/subAttnAdmin',
          title: 'MENU.SUBJECT_ATTENDANCE',
          fontIcon: 'fas fa-calendar',
          visible: hasPermission('attendance_manager'),
        },
        {
          to: isDevEnv()
            ? '/institute/data-import/annual-calendar/upload'
            : '/institute/dataupload/default/ysAdmin',
          title: 'MENU.YEARLY_SCHEDULE',
          fontIcon: 'fas fa-calendar',
          visible:
            hasPermission('schedule_manager') &&
            hasPermission('data_upload_manager_yearly_schedules') &&
            !hideImportAnnualCalendar,
        },
        {
          to: '/institute/dataupload/default/prevGradesAdmin',
          title: 'MENU.STUDENT_PREVIOUS_GRADE',
          fontIcon: 'fas fa-calendar',
          visible: !hideImportStudentPrevGrade,
        },
        {
          to: '/institute/dataupload/default/prevPercentagesAdmin',
          title: 'MENU.STUDENT_PREVIOUS_PERCENTAGES',
          fontIcon: 'fas fa-calendar',
          visible: !hideImportStudentPrevPercentages,
        },
        {
          to: '/institute/dataupload/default/soAdmin',
          title: 'MENU.SUBJECTS_OUTLINE',
          fontIcon: 'fas fa-file',
          visible:
            hasPermission('syllabus_manager') &&
            hasPermission('data_upload_manager_subject_outlines'),
        },
        {
          to: '/institute/dataupload/default/discountAdmin',
          title: 'MENU.DISCOUNT',
          fontIcon: 'fas fa-file',
          visible: hasPermission('fee_manager'),
        },
        {
          // to: '/institute/data-import/tags/upload',
          to: '/institute/dataupload/default/tagsAdmin',
          title: 'MENU.TAGS',
          fontIcon: 'fas fa-tags',
          visible: hasPermission('tags_manager') && !hideImportTags,
        },
        {
          to: isDevEnv()
            ? '/institute/data-import/store-items/upload'
            : '/institute/dataupload/default/inAdmin',
          title: 'MENU.STORE_ITEMS',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('inventory_manager'),
        },
        {
          to: '/institute/data-import/student-homework/upload',
          title: 'MENU.STUDENT_HOMEWORK',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('homework_manager'),
        },
        {
          to: '/institute/data-import/student-test/upload',
          title: 'MENU.STUDENT_TEST',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('test_manager'),
        },
        {
          // to: '/institute/data-import/student-exam/upload',
          to: '/institute/dataupload/default/examAdmin',
          title: 'MENU.STUDENT_EXAM',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('exam_manager'),
        },
        {
          to: '/institute/dataupload/default/cgAdmin',
          title: 'MENU.CAIE_CONSOLIDATED_RESULT',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('exam_manager'),
        },
        {
          to: '/institute/dataupload/default/scAdmin',
          title: 'MENU.CAIE_SUBJECT_COMPONENTS_RESULT',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('exam_manager'),
        },
        {
          to: '/institute/dataupload/default/prvAdmin',
          title: 'MENU.POST_REPORT_CARD_VARIABLE_DATA',
          fontIcon: 'fas fa-file-alt',
          visible: hasPermission('exam_manager'),
        },
        {
          to: '/institute/dataupload/manualJournalUpload/BulkMj',
          title: 'MENU.ACCOUNTS_IMPORT',
          fontIcon: 'fas fa-file-import',
          visible: hasPermission('account_manager'),
        },
        {
          to: '/institute/data-import/csv-import/upload',
          title: 'MENU.CSV_IMPORT',
          fontIcon: 'fas fa-file-excel',
          visible: isDevEnv() && isAIDevEnv() && hasPermission('ai_timetable'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.SYSTEM_REPORTS',
      fontIcon: 'fas fa-folder',
      visible: hasPermission('system_reports') || hasPermission('system_reports_jobs_summary'),
      subLinks: [
        {
          to: '/institute/branch-report',
          title: 'MENU.BRANCH_REPORT',
          fontIcon: 'fas fa-file',
          visible: hasPermission('system_reports_branch_utilization_reports'),
        },
        {
          to: '/institute/system-logs',
          title: 'MENU.SYSTEM_LOGS_REPORTS',
          fontIcon: 'fas fa-history',
          visible: hasPermission('system_reports_jobs_summary'),
        },
        {
          to: '/institute/notificationLog/admin',
          title: 'MENU.NOTIFICATIONS_REPORT',
          fontIcon: 'fas fa-file',
          visible: hasPermission('system_reports_notification_reports'),
        },
        {
          to: '/institute/smsLog/admin',
          title: 'MENU.SMS_REPORT',
          fontIcon: 'fas fa-file',
          visible: hasPermission('system_reports_sms_reports'),
        },
        {
          to: '/institute/whatsappLog/admin',
          title: 'MENU.WHATSAPP_LOG',
          fontIcon: 'fas fa-file',
          visible: isAdmin() || (isStaff() && hasPermission('system_reports')),
        },
        {
          to: '/institute/dblog/admin',
          title: 'MENU.DATABASE_LOG',
          fontIcon: 'fas fa-file',
          visible: isAdmin() || (isStaff() && hasPermission('system_reports_database_logs')),
        },
        {
          to: '',
          title: 'MENU.BIOMETRIC',
          fontIcon: 'fas fa-folder',
          visible: isAdmin(),
          subLinks: [
            {
              to: '/institute/activeRecordLog/admin',
              title: 'MENU.BIOMETRIC_API_ACCESS_LOG',
              fontIcon: 'fas fa-file',
            },
            {
              to: '/institute/biometricCsvLog',
              title: 'MENU.BIOMETRIC_CSV_LOG',
              fontIcon: 'fas fa-file',
            },
            {
              to: '/institute/biometricDeviceLog',
              title: 'MENU.BIOMETRIC_DEVICE_LOG',
              fontIcon: 'fas fa-file',
            },
            {
              to: '/institute/biometricLog',
              title: 'MENU.BIOMETRIC_LOG',
              fontIcon: 'fas fa-file',
            },
            // {
            //   to: '/institute/BiometricCsvLogs/upload',
            //   title: 'MENU.BIOMETRIC_FILE_UPLOAD',
            //   fontIcon: 'fas fa-file',
            // },
            {
              to: '/institute/BiometricCsvLogs/attendanceUpload',
              title: 'MENU.ATTENDANCE_FILE_UPLOAD',
              fontIcon: 'fas fa-file',
            },
            {
              to: '/institute/diagnose/admin',
              title: 'MENU.DIAGNOSES',
              fontIcon: 'fas fa-file',
            },
          ],
        },
      ],
    },

    // {
    //   to: `/institute/contact-support?${queryParams}`,
    //   title: 'MENU.CONTACT_SUPPORT',
    //   fontIcon: 'fa fa-headset',
    //   visible: hasPermission('support_module'),
    // },

    // {
    //   to: 'https://ilmversity.com/knowledge-base/',
    //   target: '_blank',
    //   external: true,
    //   title: 'MENU.KNOWLEDGE_BASE',
    //   fontIcon: 'fas fa-book-open',
    //   hasBullet: false,
    //   visible: hasPermission('knowledge_base'),
    // },

    // Kenzer Roots SSO: shown once the school admin connected the integration
    // (My Profile > Integrations). Opens in a new tab so the portal stays open;
    // the page there mints a fresh token and redirects.
    {
      to: '/integrations/kenzer-roots/launch',
      target: '_blank',
      title: 'MENU.KENZER_ROOTS',
      fontIcon: 'fas fa-graduation-cap',
      visible: generalSettings?.kenzer_roots_sso === 1 && (isAdmin() || isStaff() || isTeacher()),
    },

    {
      to: '/user-manual',
      title: 'MENU.USER_MANUAL',
      fontIcon: 'fas fa-book-open',
    },
  ]
}

/**
 * Hide expandable parents whose sub-links are all hidden, and section labels
 * whose entire group (the items up to the next label) is hidden. Without this,
 * a role that has a module permission but none of the finer leaf permissions
 * renders an empty dropdown or a dangling section header.
 */
const pruneEmptyMenuGroups = (links: Link[]): Link[] => {
  const withParents = links.map((link) =>
    link.subLinks
      ? {...link, visible: link.visible && link.subLinks.some((sub) => sub.visible)}
      : link
  )

  return withParents.map((link, index) => {
    if (link.type !== 'label') return link
    let groupHasVisible = false
    for (let i = index + 1; i < withParents.length; i++) {
      if (withParents[i].type === 'label') break
      if (withParents[i].visible) {
        groupHasVisible = true
        break
      }
    }
    return {...link, visible: link.visible && groupHasVisible}
  })
}

export const getStudentLink = ({mainReducer}: {mainReducer: MainReducer}): Link[] => {
  const {generalSettings} = mainReducer
  const links: Link[] = [
    {
      to: '/dashboard',
      title: 'MENU.DASHBOARD',
      fontIcon: 'fas fa-home',
      visible: hasPermission('dashboard'),
    },

    /**
     * Administration
     */
    {
      type: 'label' as const,
      title: 'MENU.ADMINISTRATION',
      to: '',
      visible:
        hasPermission('attendance_manager') ||
        hasPermission('digital_request_manager') ||
        hasPermission('news_manager') ||
        hasPermission('schedule_manager'),
    },

    {
      to: '',
      title: 'MENU.ATTENDANCE',
      fontIcon: 'far fa-edit',
      visible: hasPermission('attendance_manager'),
      subLinks: [
        {
          to: '/attendance/studentAttendReport',
          title: 'MENU.STUDENT_ATTENDANCE_REPORTS',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_student_attendance_report') &&
            hasPermission('attendance_manager_view'),
        },
        {
          to: '/attendance/studentSubjectAttendReport',
          title: 'MENU.STUDENT_SUBJECT_ATTENDANCE_REPORTS',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_student_subject_attendance_report') &&
            generalSettings?.subject_attendance === GENERAL_ENABLE_DISABLE.ENABLE &&
            hasPermission('attendance_manager_view'),
        },
        {
          // Mobile More "Submitted attendance requests" (hidden on LGS there too)
          to: '/attendance/viewRequests',
          title: 'SP_ATT.AMENDMENT.LIST_TITLE',
          fontIcon: 'fas fa-copy',
          visible:
            hasPermission('attendance_manager_student_attendance_report') &&
            hasPermission('attendance_manager_view') &&
            !isLgs(),
        },
      ],
    },
    /**
     * College Counselling
     */
    {
      to: '/institute/counselling/portfolio',
      title: 'MENU.COUNSELLING',
      fontIcon: 'fas fa-graduation-cap',
      visible: hasPermission('college_counselling'),
    },

    {
      // Native digital-request module (shared with the school side). The two
      // legacy sub-links both landed on a static "use the mobile app" notice.
      to: '/institute/digital-requests/list',
      title: 'MENU.LEAVE_APPLICATIONS',
      fontIcon: 'fas fa-envelopes-bulk',
      visible: hasPermission('digital_request_manager'),
    },

    {
      to: '/institute/news/fullmode',
      title: 'MENU.NEWS',
      fontIcon: 'far fa-newspaper',
      visible: hasPermission('news_manager'),
    },

    {
      to: '',
      title: 'MENU.SCHEDULES',
      fontIcon: 'far fa-calendar-alt',
      visible: hasPermission('schedule_manager'),
      subLinks: [
        {
          to: '/institute/schedule/yearly-calendar',
          title: 'MENU.YEARLY_SCHEDULE',
          fontIcon: 'far fa-calendar-alt',
          visible: hasPermission('schedule_manager'),
        },
        {
          to: '/institute/schedule/daily-calendar',
          title: 'MENU.DAILY_SCHEDULE',
          fontIcon: 'far fa-calendar-alt',
          visible: hasPermission('schedule_manager'),
        },
      ],
    },

    /**
     * Academics
     */
    {
      type: 'label' as const,
      title: 'MENU.ACADEMICS',
      to: '',
      visible:
        hasPermission('exam_manager') ||
        hasPermission('activity_manager') ||
        hasPermission('extra_curricular_activities') ||
        hasPermission('homework_manager') ||
        hasPermission('performance_report') ||
        hasPermission('test_manager') ||
        hasPermission('video_lecture'),
    },

    {
      to: '',
      title: 'MENU.ACTIVITY_MANAGEMENT',
      fontIcon: 'fa-solid fa-tasks',
      visible: hasPermission('activity_manager'),
      subLinks: [
        {
          to: '/activities',
          title: 'MENU.ACTIVITY_MANAGEMENT',
          fontIcon: 'fas fa-tasks',
          visible: hasPermission('activity_manager'),
        },
        {
          to: '/activityAttendance/studentActivityAttendReport',
          title: 'STUDENT_ATTENDANCE.ACTIVITY_REPORTS',
          fontIcon: 'fas fa-file',
          visible: hasPermission('activity_manager'),
        },
      ],
    },

    {
      to: '/subjects',
      title: 'MENU.SUBJECT',
      fontIcon: 'fas fa-cog',
      visible: hasPermission('subject_manager'),
    },

    // The NEW student/parent exam pages, in PARALLEL with the legacy links in
    // the group below (which point at /exam/... and fall through to the
    // iframe). Same gate as the school-side beta group.
    {
      to: '',
      title: customTitle('MENU.EXAM_MANAGER_BETA'),
      fontIcon: 'fas fa-flask',
      visible: isNewExamManagerEnabled() && hasPermission('exam_manager'),
      subLinks: [
        {
          to: '/institute/exam/admin',
          title: 'MENU.EXAM',
          fontIcon: 'fas fa-file',
          visible: hasPermission('exam_manager_exams'),
        },
        {
          to: '/institute/exam/report-cards',
          title: 'MENU.EXAM_REPORT_CARD_NEW',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/institute/exam/performance',
          title: 'MENU.PERFORMANCE_REPORTS',
          fontIcon: 'far fa-chart-bar',
          visible: hasPermission('performance_report_exam_reports'),
        },
      ],
    },

    {
      to: '',
      title: customTitle('MENU.EXAMS'),
      fontIcon: 'fas fa-copy',
      /* Same rule the school menu uses. Without the flag check a student on an
         opted-in tenant saw BOTH groups, with "Exam" and "Exam Report Card"
         listed twice over — once pointing at the new module and once at the
         legacy pages. Dev still shows both, which is the point of the
         isDevEnv() arm. */
      visible: (isDevEnv() || !isNewExamManagerEnabled()) && hasPermission('exam_manager'),
      subLinks: [
        {
          to: '/exams',
          title: 'MENU.EXAM',
          fontIcon: 'fas fa-file',
          // Lyceum hotfix: the school asked for the student/parent exam list
          // to be hidden even while the exam module is on. Hard-coded to the
          // lyceum tenant only; the route is blocked in StudentRoutes too.
          visible: hasPermission('exam_manager_exams') && !isLyceum(),
        },
        {
          to: '/exam/report',
          title: 'MENU.NEW_EXAM_REPORT_CARD',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('old_reportcard'),
        },
        {
          to: '/exam/reportcardnew',
          title: 'MENU.EXAM_REPORT_CARD_NEW',
          fontIcon: 'fas fa-copy',
          visible: hasPermission('exam_manager_exam_report_cards'),
        },
        {
          to: '/exam/datesheet',
          title: 'MENU.EXAM_SCHEDULE',
          fontIcon: 'fas fa-calendar',
          visible: hasPermission('exam_manager_exam_schedules'),
        },
      ],
    },

    {
      to: '',
      title: 'MENU.EX-CURRICULAR_ACTIVITIES',
      fontIcon: 'fas fa-layer-group',
      visible: hasPermission('extra_curricular_activities'),
      subLinks: [
        {
          to: '/site/stream',
          title: 'MENU.LIVE_STREAMING',
          fontIcon: 'fas fa-bolt',
          visible: hasPermission('extra_curricular_activities_live_streaming'),
        },
        {
          to: '/institute/events/highlights',
          title: 'MENU.EVENTS',
          fontIcon: 'la la-photo',
          visible: hasPermission('extra_curricular_activities_photo_gallery'),
        },
      ],
    },

    {
      to: '/homework/admin',
      title: customTitle('MENU.HOMEWORKS'),
      fontIcon: 'fas fa-book-open',
      visible: hasPermission('homework_manager'),
    },

    {
      to: '',
      title: 'MENU.PERFORMANCE_REPORTS',
      fontIcon: 'far fa-copy',
      visible: hasPermission('performance_report'),
      subLinks: [
        {
          to: '/reports/homework',
          title: 'MENU.HOMEWORK_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_homework_reports'),
        },
        {
          to: '/reports/test',
          title: 'MENU.TEST_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_test_reports'),
        },

        {
          to: '/reports/exam',
          title: 'MENU.EXAM_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_exam_reports'),
        },

        {
          to: '/reports/overall',
          title: 'MENU.OVERALL_REPORT',
          fontIcon: 'far fa-copy',
          visible: hasPermission('performance_report_overall_reports'),
        },
        {
          to: '/institute/performance-reports',
          title: 'MENU.ALL_REPORTS',
          fontIcon: 'far fa-copy',
          visible: isDevEnv() && hasPermission('performance_report_overall_reports'),
        },
      ],
    },

    {
      to: '/test/admin',
      title: customTitle('MENU.TESTS'),
      fontIcon: 'fas fa-address-book',
      visible: hasPermission('test_manager'),
    },

    {
      // One entry, as in the mobile app: the page opens on Live with a
      // Recorded tab beside it.
      to: '/course/lectures',
      title: 'MENU.VIDEO_LECTURES',
      fontIcon: 'fas fa-film',
      visible: hasPermission('video_lecture'),
    },

    /**
     * Finance
     */
    {
      type: 'label' as const,
      title: 'MENU.FINANCE',
      to: '',
      visible: hasPermission('fee_manager'),
    },

    {
      to: getStudentFeePath(),
      title: 'MENU.FEE_SECTION',
      fontIcon: 'fas fa-wallet',
      visible: hasPermission('fee_manager') && !isLgs(),
    },
    {
      to: '/fee',
      title: 'MENU.FEE_SECTION',
      fontIcon: 'fas fa-wallet',
      visible: hasPermission('fee_manager') && isLgs(),
    },

    {
      to: '/site/lgsFee',
      title: 'MENU.FEES',
      fontIcon: 'fas fa-briefcase',
      visible: hasPermission('fee_manager') && isLgs(),
    },

    /**
     * Management
     */
    {
      type: 'label' as const,
      title: 'MENU.MANAGEMENT',
      to: '',
      visible:
        hasPermission('blog_manager') ||
        hasPermission('school_blog') ||
        hasPermission('notification_manager') ||
        hasPermission('message_manager_view'),
    },

    {
      to: '/blogs/blogs',
      title: 'MENU.BLOGS',
      fontIcon: 'far fa-comment-dots',
      visible: hasPermission('school_blog'),
    },

    {
      to: '/notifications',
      title: 'MENU.NOTIFICATIONS',
      fontIcon: 'fa fa-bell',
      visible: hasPermission('notification_manager'),
    },

    {
      to: '/messages',
      title: 'MENU.MESSAGES',
      fontIcon: 'far fa-envelope',
      visible: hasPermission('message_manager_view'),
    },

    /**
     * Parenting Section (parent users only — native pages)
     */
    {
      to: '',
      title: 'MENU.PARENTING_SECTION',
      fontIcon: 'fas fa-hands-helping',
      visible: isParent() && hasPermission('parenting_section'),
      subLinks: [
        {
          to: '/parenting/default/',
          title: 'MENU.DASHBOARD',
          fontIcon: 'fas fa-home',
          visible: true,
        },
        {
          to: '/parenting/classes/',
          title: 'MENU.PARENTING_CLASSES',
          fontIcon: 'fas fa-chalkboard',
          visible: true,
        },
        {
          to: '/parenting/default/tip',
          title: 'PARENTING.TIP_OF_THE_DAY',
          fontIcon: 'far fa-lightbulb',
          visible: true,
        },
        {
          to: '/parenting/default/article',
          title: 'PARENTING.ARTICLE_OF_THE_WEEK',
          fontIcon: 'far fa-newspaper',
          visible: true,
        },
        {
          to: '/parenting/default/testimonial/',
          title: 'MENU.PARENTING_TESTIMONIALS',
          fontIcon: 'far fa-comments',
          visible: true,
        },
        {
          to: '/parenting/blog',
          title: 'MENU.PARENTING_BLOG',
          fontIcon: 'far fa-comment-dots',
          visible: true,
        },
        {
          to: '/parenting/default/notifications',
          title: 'MENU.NOTIFICATIONS',
          fontIcon: 'fa fa-bell',
          visible: true,
        },
        {
          to: '/parenting/default/expertopinion',
          title: 'MENU.PARENTING_EXPERT_OPINION',
          fontIcon: 'fas fa-user-md',
          visible: true,
        },
      ],
    },

    /**
     * Other
     */
    {
      type: 'label' as const,
      title: 'MENU.OTHER',
      to: '',
      visible: true,
    },

    {
      to: '/class-history',
      title: 'MENU.STUDENT_HISTORY',
      fontIcon: 'far fa-calendar-alt',
      visible: hasPermission('student_history'),
    },

    {
      to: '/tutorials',
      title: 'MENU.TUTORIALS',
      fontIcon: 'fas fa-book',
      visible: hasPermission('knowledge_base'),
    },

    {
      to: '/user-manual',
      title: 'MENU.USER_MANUAL',
      fontIcon: 'fas fa-book-open',
    },

    {
      to: '/help/faqs',
      title: 'SP_MISC.HELP.FAQS',
      fontIcon: 'far fa-question-circle',
      visible: true,
    },

    {
      to: '/help/contact',
      title: 'SP_MISC.CONTACT.TITLE',
      fontIcon: 'far fa-life-ring',
      visible: true,
    },
  ]

  return pruneEmptyMenuGroups(links)
}

export const getAsideMenuLinks = ({
  mainReducer,
  userReducer,
}: {
  mainReducer: MainReducer
  userReducer: UserReducer
}): Link[] => {
  const {userType} = userReducer

  if ([USER_TYPES.ADMIN, USER_TYPES.STAFF, USER_TYPES.TEACHER].includes(userType)) {
    return getSchoolLink({mainReducer})
  }

  if ([USER_TYPES.STUDENT, USER_TYPES.PARENT].includes(userType)) {
    return getStudentLink({mainReducer})
  }

  return []
}
